import { useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Hook to scroll to the anchor element and scroll to top on page change
 */
function useScrollNavigation() {
	const location = useLocation()

	useLayoutEffect(() => {
		const hash = location.hash.slice(1)

		if (hash) {
			scrollToElement(hash)
		} else {
			scrollToTop()
		}
	}, [location])

	return null
}

/**
 * Scrolls to element with readiness checks
 */
function scrollToElement(elementId: string) {
	const maxAttempts = 30 // Max 500ms delay at 60fps - should be more than enough for the element to be ready
	let attempts = 0

	function attemptScroll() {
		attempts++
		const element = document.getElementById(elementId)

		if (element && isElementReady(element)) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' })
			return
		}

		if (attempts < maxAttempts) {
			requestAnimationFrame(attemptScroll)
		} else {
			// Fallback: scroll to top if element never becomes ready
			console.warn(`Element with id "${elementId}" not found or not ready after ${maxAttempts} attempts`)
			scrollToTop()
		}
	}

	requestAnimationFrame(attemptScroll)
}

function scrollToTop() {
	// Wait one frame to ensure page is rendered
	requestAnimationFrame(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	})
}

/**
 * Checks if element is ready for scrolling
 */
function isElementReady(element: HTMLElement): boolean {
	const rect = element.getBoundingClientRect()

	return rect.width > 0 && rect.height > 0
}

export default useScrollNavigation
